import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { useLocalStorage } from "@/components/helpers/custom-hooks/use-local-storage";
import { useClientWorkspacesQuery } from "@/graphql";
import { INDIVIDUAL_ID } from "@/types/filter";
import { DEFAULT_PERSONAL_WOKSPACE, Workspace } from "@/types/workspaces";

interface ClientWorkspaceContextValue {
  currentWorkspaceId: string;
  workspaces: Workspace[];
  onSelectWorkspace: (workspaceId: string) => void;
  loading: boolean;
}

const ClientWorkspaceContext =
  createContext<ClientWorkspaceContextValue | null>(null);

interface ClientWorkspaceProviderProps {
  children: ReactNode;
  clientId?: string | null;
}

export function ClientWorkspaceProvider({
  children,
  clientId,
}: ClientWorkspaceProviderProps) {
  const user = useCurrentUser();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [hasProcessedData, setHasProcessedData] = useState(false);

  const [currentWorkspaceId, setCurrentWorkspaceId] = useLocalStorage<
    string | undefined
  >(clientId ? `ClientCurrentWorkspace_${clientId}` : "ClientCurrentWorkspace");

  const onSelectWorkspace = (workspaceId: string) => {
    if (workspaces.find((item) => item.id === workspaceId)) {
      setCurrentWorkspaceId(workspaceId);
    } else if (workspaces.length > 0) {
      // Default to first available workspace if selection is invalid
      setCurrentWorkspaceId(workspaces[0].id);
    }
  };

  const isSuperuser = user ? user.isSuperuser : false;

  const { data, loading: queryLoading } = useClientWorkspacesQuery({
    variables: {
      clientId: clientId || "",
    },
    skip: !clientId || !user,
  });

  useEffect(() => {
    if (data && !hasProcessedData) {
      let processedWorkspaces: Workspace[] = [];
      const hasIndividualAccount = data.customer?.hasIndividualAccount || false;

      if (isSuperuser && data.clientWorkspaces) {
        processedWorkspaces = data.clientWorkspaces;
      } else {
        processedWorkspaces = data.assignments
          .map((assignment) => assignment.workspace)
          .filter((workspace) => workspace !== null && workspace !== undefined);
      }

      // Only add Personal workspace if client has individual account
      if (hasIndividualAccount) {
        processedWorkspaces = processedWorkspaces.concat([
          DEFAULT_PERSONAL_WOKSPACE,
        ]);
      }

      // Check if current selection is valid
      const isCurrentSelectionValid = processedWorkspaces.find(
        (element) => element.id === currentWorkspaceId,
      );

      // If current selection is invalid or is Personal when user doesn't have individual account
      if (
        !isCurrentSelectionValid ||
        (currentWorkspaceId === INDIVIDUAL_ID && !hasIndividualAccount)
      ) {
        // Default to first workspace if available
        if (processedWorkspaces.length > 0) {
          setCurrentWorkspaceId(processedWorkspaces[0].id);
        }
      }

      setWorkspaces(processedWorkspaces);
      setHasProcessedData(true);
    }
  }, [data, hasProcessedData, currentWorkspaceId, isSuperuser]);

  // Loading is true if either the query is loading OR we have data but haven't processed it yet
  const loading = queryLoading || (!!data && !hasProcessedData);

  const value: ClientWorkspaceContextValue = {
    currentWorkspaceId:
      currentWorkspaceId ||
      (workspaces.length > 0 ? workspaces[0].id : INDIVIDUAL_ID),
    workspaces,
    onSelectWorkspace,
    loading,
  };

  return (
    <ClientWorkspaceContext.Provider value={value}>
      {children}
    </ClientWorkspaceContext.Provider>
  );
}

export function useClientWorkspaceContext(): ClientWorkspaceContextValue {
  const context = useContext(ClientWorkspaceContext);
  if (!context) {
    throw new Error(
      "useClientWorkspaceContext must be used within a ClientWorkspaceProvider",
    );
  }
  return context;
}
